import React, { useState, useEffect, useMemo } from "react";
import "./webReport.css";
import "./printStyles.css";
import MetaDataSection from "./metaDataSection";
import ItemSection from "./itemSection";
import CoverPage from "./coverPage";
import PrintHeader from "./PrintHeader";
import PrintFooter from "./PrintFooter";
import PrintTableOfContents from "./PrintTableOfContents";
import { calculateSticky, formattedDate } from "./helperFunctions";
import Item from "../../components/Item";
import ConcerningLevelBadge from "../../components/ConcerningLevelBadge";

const ReportContent = ({
  challenges,
  metaDataItems,
  issueItems,
  activeSection,
  foundSection,
  foundSubsection,
  activeIssueInView,
  isMobile,
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const [showIssueTitleInStickyHeader, setShowIssueTitleInStickyHeader] =
    useState(false);

  // Extract CoverPage data from metaDataItems
  const {
    Agents: agents,
    Client: client,
    Company: company,
    "Profile Image": profileImage,
    Address: address,
    User: user,
    Disclaimer: disclaimer,
    "Project Information": projectInfo,
  } = metaDataItems;

  const companyName = company?.name;
  const jobAddress = address?.formatted_address;

  // Build sequential section numbers map (sectionId → number)
  const sectionNumbers = useMemo(() => {
    const entries = [];

    if (disclaimer) {
      entries.push("inspection-scope-disclaimer");
    }
    entries.push("definitions-glossary");
    entries.push("inspection-overview");
    entries.push("challenge-summary-section");
    entries.push("property-details");

    (issueItems || []).forEach((section) => {
      entries.push(
        `section-${section.name?.toLowerCase().replace(/\s+/g, "-")}`
      );
    });

    const map = {};
    entries.forEach((id, index) => {
      map[id] = index + 1;
    });
    return map;
  }, [disclaimer, issueItems]);

  // Scroll listener and print preparation
  useEffect(() => {
    const handleScroll = () => {
      const activeSectionEl = document.getElementById(activeSection);

      if (!activeSectionEl) return;

      let showSticky = false;

      if (["property-details", "inspection-overview"].includes(activeSection)) {
        const titleSelector =
          activeSection === "property-details" ? ".title-wrapper" : ".item";
        showSticky = calculateSticky(isMobile, activeSectionEl, titleSelector);
        setShowIssueTitleInStickyHeader(false);
      } else {
        showSticky = calculateSticky(
          isMobile,
          activeSectionEl,
          ".title-wrapper",
          ".subtitle"
        );

        const activeSectionTitleRect = activeSectionEl
          .querySelector(".item")
          ?.getBoundingClientRect();

        const passedHeight = isMobile ? 24 : -44;
        if (activeSectionTitleRect?.top <= passedHeight) {
          setShowIssueTitleInStickyHeader(true);
        } else {
          setShowIssueTitleInStickyHeader(false);
        }
      }

      setIsSticky(showSticky);
    };

    const handleBeforePrint = () => {
      setIsSticky(false);
    };

    const handlePreparePrint = () => {
      window.print();
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("preparePrintReport", handlePreparePrint);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("preparePrintReport", handlePreparePrint);
    };
  }, [activeSection, isMobile]);

  return (
    <div>
      <div
        className={`sticky-header-wrapper header ${isSticky ? "sticky" : ""}`}
      >
        <div>
          {foundSection?.name}
          {foundSubsection ? " - " : ""}
          {foundSubsection?.name}
          {showIssueTitleInStickyHeader && activeIssueInView && (
            <Item
              text={activeIssueInView.name || activeIssueInView.id}
              children={null}
              showBadge
              badgeContent={
                <ConcerningLevelBadge
                  concerningLevel={activeIssueInView.concern_level || 0}
                  itemType={activeIssueInView.type}
                />
              }
              showDivider={false}
            />
          )}
        </div>
      </div>
      <div className="main-content">
        {/* Cover page rendered outside table - no print header/footer */}
        <div className="metadata-container" id="section-cover-page">
          <CoverPage
            agents={agents}
            client={client}
            company={company}
            profileImage={profileImage}
            address={address}
            user={user}
            inspectedDate={formattedDate(projectInfo?.created_at)}
          />
        </div>

        {/* Fixed footer for print — divider line + TOC link on every page */}
        <PrintFooter />
        <div className="print-footer-toc">
          <a href="#print-toc" className="print-footer-toc-link">
            &uarr; Table of Contents
          </a>
        </div>

        {/* TOC page: outside table, with its own header */}
        <div className="print-toc-page">
          <div className="print-header-content">
            <span className="print-header-left">{companyName}</span>
            <span className="print-header-right">{jobAddress}</span>
          </div>
          <hr className="print-header-divider" />
          <PrintTableOfContents
            disclaimer={disclaimer}
            issueItems={issueItems}
          />
        </div>

        {/* Table wrapper for print header/tfoot repetition on content pages */}
        <table className="print-table-wrapper">
          <PrintHeader companyName={companyName} jobAddress={jobAddress} />
          <tbody>
            <tr>
              <td>
                <MetaDataSection
                  metaDataItems={metaDataItems}
                  challenges={challenges}
                  sectionNumbers={sectionNumbers}
                />
                <ItemSection
                  issueItems={issueItems}
                  sectionNumbers={sectionNumbers}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReportContent;
